import { Typography } from '@mui/material';
import Button from 'components/Button';
import { useFormWithStateContext } from 'contexts/FormWithStateContext';
import { CapitalActionEntryType } from 'further-types/lp/capital-action';
import { WaterfallStructure } from 'further-types/lp/carry-settings';
import { CurrencyCode } from 'further-types/lp/vehicle';
import { Column, Divider, Row } from 'further-ui/components/Layout';
import NumberInput from 'further-ui/components/NumberInput';
import Table from 'further-ui/components/Table';
import TypographyButton from 'further-ui/components/TypographyButton';
import { useDisclosure } from 'further-ui/hooks';
import {
  CalendarDay,
  getDistributionWaterfallElementValue,
  numberToCurrencyString,
} from 'further-ui/utils';
import useVehicle from 'hooks/lp/data/vehicle/useVehicle';
import api from 'lib/trpcClient';
import { round, sumBy } from 'lodash';
import { useState } from 'react';
import downloadXlxs from 'utils/downloadXlxs';
import {
  CapitalActionFormState,
  UpdateCapitalActionFormValues,
} from '../../../hooks/useCapitalActionForm';
import useSyncInitialDistributionValues from '../hooks/useSyncInitialDistributionValues';
import OutsideOfCarryDistribution from './OutsideOfCarryDistribution';
import UploadManualOutputsModal, {
  FundingsRemainingToDistribute,
} from './UploadManualOutputsModal';
import WaterfallCalculations from './WaterfallCalculations';
import PageSection from 'components/new/PageSection';

type Props = {
  index: number;
  vehicleId: string;
  entryType: CapitalActionEntryType;
  baseCurrency: CurrencyCode;
  ledgerDate: CalendarDay;
};

const CapitalDistributionDetails: React.FC<Props> = ({
  vehicleId,
  ledgerDate,
  baseCurrency,
  index,
}) => {
  const [showCarriedInterestWaterfall, setShowCarriedInterestWaterfall] =
    useState(false);
  const uploadModalDisclosure = useDisclosure();

  const { vehicle } = useVehicle(vehicleId);

  const {
    formMethods: { watch, getValues, setValue, formState },
  } = useFormWithStateContext<
    UpdateCapitalActionFormValues,
    CapitalActionFormState
  >();

  const entries = getValues('entries') ?? [];

  const [fundsRemainingToDistribute] =
    api.capitalAction.getFundsRemainingToDistribute.useSuspenseQuery({
      vehicleId,
      date: ledgerDate,
      entries: entries.filter(
        (entry) =>
          entry.entryType !== CapitalActionEntryType.CapitalDistribution,
      ),
    });

  const exportCalculations =
    api.capitalAction.exportCalculationsFile.useMutation({
      onSuccess: (data) => {
        downloadXlxs(
          data.file,
          `Carried interest calculations - ${
            vehicle?.vehicleName
          } - ${ledgerDate.toLabel()}`,
        );
      },
    });

  const [
    fundsRemainingToDistributeOverrideValues,
    setFundsRemainingToDistributeOverrideValues,
  ] = useState<Array<FundingsRemainingToDistribute>>([]);

  const funds =
    fundsRemainingToDistributeOverrideValues.length > 0
      ? fundsRemainingToDistributeOverrideValues
      : fundsRemainingToDistribute;

  const { setTotalAmountAndCapitalBreakdown } =
    useSyncInitialDistributionValues({
      fundsRemainingToDistribute: funds,
      entryIndex: index,
    });

  const handleAmountChange = (
    commitmentId: string,
    amount: number,
    field:
      | 'subjectToCarry'
      | 'outsideOfCarry'
      | 'recallableOutsideOfCarry'
      | 'recallableSubjectToCarry',
  ) => {
    const newBreakdown =
      getValues(`entries.${index}.capitalBreakdown`)?.map((breakdownValue) => {
        if (breakdownValue.commitmentId === commitmentId) {
          const newValue = {
            subjectToCarry:
              breakdownValue.fundsDistributed?.subjectToCarry ?? 0,
            outsideOfCarry:
              breakdownValue.fundsDistributed?.outsideOfCarry ?? 0,
            recallableOutsideOfCarry:
              breakdownValue.fundsDistributed?.recallableOutsideOfCarry ?? 0,
            recallableSubjectToCarry:
              breakdownValue.fundsDistributed?.recallableSubjectToCarry ?? 0,
          };
          newValue[field] = amount;

          return {
            commitmentId: breakdownValue.commitmentId,
            fundsDistributed: newValue,
          };
        }
        return breakdownValue;
      }) ?? [];

    setValue(`entries.${index}.capitalBreakdown`, newBreakdown);

    setValue(
      `entries.${index}.totalAmountLcy`,
      round(
        sumBy(
          newBreakdown,
          (breakdown) =>
            (breakdown.fundsDistributed?.subjectToCarry ?? 0) +
            (breakdown.fundsDistributed?.outsideOfCarry ?? 0),
        ),
        2,
      ),
    );
  };

  const distributionValues = Object.fromEntries(
    (watch(`entries.${index}.capitalBreakdown`) ?? []).map((row) => [
      row.commitmentId,
      {
        total: row.fundsDistributed?.subjectToCarry ?? 0,
        recallable: row.fundsDistributed?.recallableSubjectToCarry ?? 0,
      },
    ]),
  );

  const columns = [
    {
      key: 'investorName',
      label: 'Investor',
      width: 200,
    },
    {
      key: 'totalDistributable',
      label: 'Total distributable',
      width: 160,
    },
    {
      key: 'undistributed',
      label: 'Undistributed',
      width: 160,
    },
    {
      key: 'distribution',
      label: 'Distribution',
      width: 180,
      render: (row: { commitmentId: string }) => (
        <NumberInput
          value={distributionValues[row.commitmentId]?.total ?? 0}
          onChange={(value) => {
            handleAmountChange(row.commitmentId, value ?? 0, 'subjectToCarry');
          }}
          decimals={2}
          placeholder="Enter amount"
          currency={baseCurrency}
          currencyStartAdornment
          clearZeroValueOnFocus
        />
      ),
    },
    {
      key: 'recallableDistribution',
      label: 'Of which recallable',
      width: 180,
      withVerticalBorder: true,
      render: (row: { commitmentId: string }, rowIndex: number) => (
        <NumberInput
          value={distributionValues[row.commitmentId]?.recallable ?? 0}
          onChange={(value) => {
            handleAmountChange(
              row.commitmentId,
              value ?? 0,
              'recallableSubjectToCarry',
            );
          }}
          decimals={2}
          placeholder="Enter amount"
          currency={baseCurrency}
          currencyStartAdornment
          clearZeroValueOnFocus
          error={
            !!formState.errors.entries?.[index]?.capitalBreakdown?.[rowIndex]
              ?.fundsDistributed?.recallableSubjectToCarry
          }
        />
      ),
    },
    {
      key: 'returnOfCapital',
      label: 'Return of capital',
      width: 160,
    },
    {
      key: 'preferredReturn',
      label: 'Preferred return',
      width: 160,
    },
    {
      key: 'extraProfits',
      label: 'Extra profits',
      width: 160,
    },
  ];

  const rows = funds.map((details) => {
    const returnOfCapital = getDistributionWaterfallElementValue(
      details.waterfall.waterfallElements,
      WaterfallStructure.ReturnOfCapital,
    );

    const preferredReturn = getDistributionWaterfallElementValue(
      details.waterfall.waterfallElements,
      WaterfallStructure.PreferredReturn,
    );

    const extraProfits = getDistributionWaterfallElementValue(
      details.waterfall.waterfallElements,
      WaterfallStructure.ExcessProceeds,
    );

    return {
      id: details.commitmentId,
      commitmentId: details.commitmentId,
      investorName: details.investorLabel,
      returnOfCapital: numberToCurrencyString(returnOfCapital, {
        currency: baseCurrency,
      }),
      preferredReturn: numberToCurrencyString(preferredReturn, {
        currency: baseCurrency,
      }),
      extraProfits: numberToCurrencyString(extraProfits, {
        currency: baseCurrency,
      }),
      totalDistributable: numberToCurrencyString(
        details.waterfall.capitalSplit.limitedPartner.totalDistributable,
        {
          currency: baseCurrency,
        },
      ),
      undistributed: numberToCurrencyString(
        details.availableToDistributeSubjectToCarry,
        {
          currency: baseCurrency,
        },
      ),
    };
  });

  const handleExportCalculations = () => {
    exportCalculations.mutate({
      vehicleId,
      date: ledgerDate,
      entries,
    });
  };

  const handleManualOutputsUpload = (
    result: Array<FundingsRemainingToDistribute>,
  ) => {
    setTotalAmountAndCapitalBreakdown(result);
    setFundsRemainingToDistributeOverrideValues(result);
  };

  return (
    <Column spacing="lg">
      <Divider />
      <PageSection
        spacing="xs"
        title={
          <Row
            breakpoint="md"
            centered
            fullWidth
            spacing="sm"
            justify="space-between"
            alignItems="flex-start"
          >
            <Column spacing="xs">
              <Typography variant="h5">
                Process distribution (subject to carried interest)
              </Typography>
            </Column>
            <Row spacing="sm" centered>
              <Button
                onClick={() => uploadModalDisclosure.onOpen()}
                variant="outlined"
                size="large"
              >
                {fundsRemainingToDistributeOverrideValues.length > 0
                  ? 'Re-upload manual outputs'
                  : 'Upload manual outputs'}
              </Button>
              {fundsRemainingToDistributeOverrideValues.length > 0 ? (
                <Button
                  onClick={() =>
                    setFundsRemainingToDistributeOverrideValues([])
                  }
                  variant="contained"
                  color="error"
                  size="large"
                >
                  Revert to system defaults
                </Button>
              ) : (
                <Button
                  onClick={handleExportCalculations}
                  loading={exportCalculations.isPending}
                  variant="contained"
                  size="large"
                >
                  Download calculation audit sheet
                </Button>
              )}
            </Row>
          </Row>
        }
      >
        <Column spacing="lg" fullWidth>
          <Table scrollable columns={columns} rows={rows} />
          <Row spacing="xs" centered fullWidth>
            {showCarriedInterestWaterfall ? (
              <Typography variant="h5">
                Carried interest waterfall calculation
              </Typography>
            ) : null}
            <TypographyButton
              variant="body2"
              text={
                showCarriedInterestWaterfall
                  ? 'Hide'
                  : 'Show carried interest waterfall calculation'
              }
              onClick={() =>
                setShowCarriedInterestWaterfall(!showCarriedInterestWaterfall)
              }
            />
          </Row>
          {showCarriedInterestWaterfall && (
            <WaterfallCalculations
              fundsToDistribute={funds}
              baseCurrency={baseCurrency}
            />
          )}
        </Column>
      </PageSection>

      <OutsideOfCarryDistribution
        fundsRemainingToDistribute={funds}
        baseCurrency={baseCurrency}
        index={index}
        onAmountChange={handleAmountChange}
      />

      <UploadManualOutputsModal
        open={uploadModalDisclosure.isOpen}
        onClose={uploadModalDisclosure.onClose}
        onUpload={handleManualOutputsUpload}
        date={ledgerDate}
        vehicleId={vehicleId}
      />
    </Column>
  );
};

export default CapitalDistributionDetails;
